// src/components/ConnectionTestDetailPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Container, Typography, Box, Paper, Button, Chip, Divider, Alert,
  IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemIcon, ListItemText, Grid, Card, CardContent
} from '@mui/material';
import {
  ArrowBack, Refresh, CheckCircle, Error, Warning, Info,
  ExpandMore, Wifi, Security, Code, Timeline, BugReport,
  NetworkCheck, Router, Memory, StorageOutlined
} from '@mui/icons-material';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

const DetailCard = ({ title, icon, status, data, error }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle />;
      case 'error': return <Error />;
      case 'warning': return <Warning />;
      default: return <Info />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
        },
        transition: 'all 0.3s ease'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: '50%',
              bgcolor: `${getStatusColor(status)}.50`,
              color: `${getStatusColor(status)}.main`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {icon}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight="600">
                {title}
              </Typography>
              <Chip
                label={status === 'success' ? 'نجح' : status === 'error' ? 'فشل' : status === 'warning' ? 'تحذير' : 'مجهول'}
                color={getStatusColor(status)}
                size="small"
                icon={getStatusIcon(status)}
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight="600" gutterBottom>
                تفاصيل الخطأ:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                fontFamily: 'monospace', 
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {JSON.stringify(error, null, 2)}
              </Typography>
            </Alert>
          ) : data ? (
            <Accordion sx={{ 
              boxShadow: 'none', 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:before': { display: 'none' }
            }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" fontWeight="600">
                  عرض التفاصيل التقنية
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" component="pre" sx={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1
                }}>
                  {JSON.stringify(data, null, 2)}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ) : (
            <Typography variant="body2" color="text.secondary">
              لا توجد بيانات متاحة
            </Typography>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function ConnectionTestDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [testResults, setTestResults] = useState(location.state?.testResults || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!testResults) {
      console.log('No test results found, redirecting...');
      navigate('/test-connection');
    } else {
      console.log('Test results found:', testResults);
    }
  }, [testResults, navigate]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const { runFullConnectionTest } = await import('../utils/check-connection');
      const results = await runFullConnectionTest();
      setTestResults(results);
    } catch (err) {
      console.error('Connection test error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getOverallStatus = () => {
    if (!testResults?.tests) return 'unknown';
    
    const tests = Object.values(testResults.tests);
    const hasError = tests.some(result => result && !result.success);
    const allSuccess = tests.every(result => result && result.success);
    
    if (hasError) return 'error';
    if (allSuccess) return 'success';
    return 'warning';
  };

  const getOverallMessage = () => {
    const status = getOverallStatus();
    switch (status) {
      case 'success':
        return 'جميع الاختبارات نجحت! الاتصال بالخادم يعمل بشكل مثالي.';
      case 'warning':
        return 'بعض الاختبارات لديها تحذيرات. قد تواجه مشاكل محدودة.';
      case 'error':
        return 'فشلت بعض الاختبارات. يرجى التحقق من إعدادات الاتصال.';
      default:
        return 'جاري اختبار الاتصال...';
    }
  };

  if (!testResults) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          textAlign: 'center'
        }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            جاري التحميل...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            إذا لم يتم التحميل، يرجى العودة إلى صفحة اختبار الاتصال
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/test-connection')}
            sx={{ mt: 2 }}
          >
            العودة لصفحة الاختبار
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ p: 1, bgcolor: 'primary.main', borderRadius: 2, display: 'flex', alignItems: 'center' }}>
            <img src="/meta-cloud-logo.png" alt="Meta Cloud" style={{ height: 24, filter: 'brightness(0) invert(1)' }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              تفاصيل اختبار الاتصال
            </Typography>
            <Typography variant="body2" color="text.secondary">
              عرض مفصل لجميع نتائج اختبارات الاتصال
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="إعادة الاختبار">
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  bgcolor: 'primary.50',
                  '&:hover': { bgcolor: 'primary.100' },
                  transition: 'all 0.2s ease'
                }}
              >
                <Refresh sx={{
                  transform: loading ? 'rotate(360deg)' : 'rotate(0deg)',
                  transition: 'transform 1s linear'
                }} />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/connection-test')}
              sx={{ borderRadius: 2 }}
            >
              العودة
            </Button>
          </Box>
        </Box>

        {/* Overall Status */}
        <Paper sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(135deg, ${getOverallStatus() === 'success' ? '#f0fdf4' : getOverallStatus() === 'warning' ? '#fffbeb' : '#fef2f2'} 0%, #ffffff 100%)`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                p: 2,
                borderRadius: '50%',
                bgcolor: `${getOverallStatus() === 'success' ? 'success' : getOverallStatus() === 'warning' ? 'warning' : 'error'}.main`,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {getOverallStatus() === 'success' ? <CheckCircle /> : 
                 getOverallStatus() === 'warning' ? <Warning /> : <Error />}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="600">
                  حالة الاتصال العامة
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getOverallMessage()}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={getOverallStatus() === 'success' ? 'ممتاز' : 
                     getOverallStatus() === 'warning' ? 'تحذير' : 'خطأ'}
              color={getOverallStatus() === 'success' ? 'success' : 
                     getOverallStatus() === 'warning' ? 'warning' : 'error'}
              size="large"
              variant="filled"
            />
          </Box>
        </Paper>

        {/* Test Information */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 3 }}>
              <Timeline color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" fontWeight="600">
                وقت الاختبار
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(testResults.timestamp).toLocaleString('ar-SA')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 3 }}>
              <Router color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" fontWeight="600">
                عنوان الخادم
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {testResults.apiUrl}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 3 }}>
              <NetworkCheck color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" fontWeight="600">
                المنشأ
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {testResults.origin}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Detailed Test Results */}
        <Typography variant="h5" fontWeight="600" sx={{ mb: 3 }}>
          نتائج الاختبارات المفصلة
        </Typography>

        {/* Basic Connection Test */}
        <DetailCard
          title="اختبار الاتصال الأساسي"
          icon={<Wifi />}
          status={testResults.tests?.basicConnection?.success ? 'success' : 'error'}
          data={testResults.tests?.basicConnection}
          error={testResults.tests?.basicConnection?.error}
        />

        {/* CORS Test */}
        <DetailCard
          title="اختبار إعدادات CORS"
          icon={<Security />}
          status={testResults.tests?.corsTest?.success ? 'success' : 'error'}
          data={testResults.tests?.corsTest}
          error={testResults.tests?.corsTest?.error}
        />

        {/* Authentication Test */}
        <DetailCard
          title="اختبار المصادقة"
          icon={<Security />}
          status={testResults.tests?.authentication?.success ? 'success' : 'error'}
          data={testResults.tests?.authentication}
          error={testResults.tests?.authentication?.error}
        />

        {/* Recommendations */}
        {getOverallStatus() !== 'success' && (
          <Paper sx={{
            p: 3,
            mt: 4,
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: 'warning.main',
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <BugReport color="warning" />
              <Typography variant="h6" fontWeight="600" color="warning.dark">
                توصيات لحل المشاكل
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <StorageOutlined color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="تأكد من تشغيل الخادم الخلفي على المنفذ الصحيح"
                  secondary="تحقق من أن الخادم يعمل على العنوان والميناء المحددين"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Security color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="تحقق من إعدادات CORS في الخادم"
                  secondary="تأكد من أن الخادم يسمح بالطلبات من النطاق الحالي"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Code color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="تأكد من صحة عنوان URL في إعدادات التطبيق"
                  secondary="تحقق من متغير البيئة REACT_APP_API_URL"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Memory color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="تحقق من إعدادات الشبكة والجدار الناري"
                  secondary="تأكد من عدم حظر الاتصالات من قبل الجدار الناري"
                />
              </ListItem>
            </List>
          </Paper>
        )}
      </Container>
    </motion.div>
  );
}
